// v3.21-1807 · Notification Preferences page — extended for the New
// Client Email channel.  Reuses the existing v1379/v1786 layout.
// New Client Notifications area now shows Push + SMS + Email side by
// side; each channel surfaces truthful status ("Missing phone" /
// "Missing email") and cannot silently claim health when misconfigured.
import { useEffect, useState } from 'react';
import { Bell, BellOff, Smartphone, Loader2, Send, Trash2, ShieldCheck, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  pushSupported, subscribeCurrentDevice, unsubscribeCurrentDevice,
  currentSubscription,
} from '../lib/push';
import { Button } from '../components/ui/button';

const EVENT_META = [
  { key: 'new_application',        label: 'New application assigned',       desc: 'Fires when an EQF or PTC intake creates a client that lands under your ownership.' },
  { key: 'new_gmail_for_client',   label: 'New Gmail for one of my clients', desc: 'A push per new inbound Gmail message on a synchronized mailbox, only when the linked client is assigned to you.' },
  { key: 'ghost_critical_overdue', label: 'Ghost item CRITICAL or OVERDUE',  desc: 'Any Ghost Daily Organizer alert that trips into CRITICAL or OVERDUE severity for a task you own.' },
];

export default function NotificationPreferencesPage() {
  const [prefs, setPrefs] = useState(null);
  const [devices, setDevices] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, d, diag] = await Promise.all([
        api.get('/push/preferences'),
        api.get('/push/subscriptions'),
        api.get('/push/diagnostics').catch(() => ({ data: null })),
      ]);
      setPrefs(p.data);
      setDevices(d.data?.items || []);
      setDiagnostics(diag.data);
      const sub = await currentSubscription();
      setSubscribed(!!sub);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load preferences');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = { ...prefs.events, [key]: !prefs.events[key] };
    setPrefs({ ...prefs, events: next });
    setSaving(true);
    try {
      await api.put('/push/preferences', { events: next });
      toast.success('Preferences saved');
    } catch (e) {
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const enablePush = async () => {
    try {
      await subscribeCurrentDevice('This device');
      toast.success('This device is now subscribed');
      load();
    } catch (e) { toast.error(e.message || 'Subscription failed'); }
  };

  const removeDevice = async (id) => {
    if (!window.confirm('Remove this device?')) return;
    try {
      await unsubscribeCurrentDevice(id);
      toast.success('Device removed');
      load();
    } catch (e) { toast.error('Remove failed'); }
  };

  const testPush = async () => {
    try {
      const { data } = await api.post('/push/test');
      const s = data?.result?.sent || 0;
      if (s) toast.success(`Test push dispatched to ${s} device(s)`);
      else toast.info('Nothing to send — subscribe this device first');
    } catch (e) { toast.error('Test failed'); }
  };

  // v3.21-1807 · New Client Notifications admin settings — SMS + Email
  // share the same singleton (dedicated SSOT, independent of Application
  // Notifications).  UI: two side-by-side cards under one section.
  const [ncSettings, setNcSettings] = useState(null);
  const [ncBusy, setNcBusy] = useState(false);
  const [ncAddLabel, setNcAddLabel] = useState('');
  const [ncAddPhone, setNcAddPhone] = useState('');
  const [ncEmailLabel, setNcEmailLabel] = useState('');
  const [ncEmailAddr, setNcEmailAddr] = useState('');
  const loadNc = async () => {
    try {
      const { data } = await api.get('/phone-system/client-notifications/settings');
      setNcSettings(data);
    } catch (_e) {
      setNcSettings({ enabled: false, recipients: [], email_enabled: false, email_recipients: [], forbidden: true });
    }
  };
  useEffect(() => { loadNc(); }, []);
  const ncToggleEnabled = async () => {
    if (!ncSettings) return;
    setNcBusy(true);
    try {
      const next = !ncSettings.enabled;
      await api.put('/phone-system/client-notifications/settings/enabled', { enabled: next });
      await loadNc();
      toast.success(`New Client SMS Alerts ${next ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setNcBusy(false); }
  };
  const ncToggleEmailEnabled = async () => {
    if (!ncSettings) return;
    setNcBusy(true);
    try {
      const next = !ncSettings.email_enabled;
      await api.put('/phone-system/client-notifications/settings/email-enabled', { enabled: next });
      await loadNc();
      toast.success(`New Client Email Alerts ${next ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setNcBusy(false); }
  };
  const ncAddRecipient = async () => {
    if (!ncAddLabel.trim() || !ncAddPhone.trim()) { toast.error('Label and mobile number required'); return; }
    setNcBusy(true);
    try {
      await api.post('/phone-system/client-notifications/recipients', {
        label: ncAddLabel.trim(), phone: ncAddPhone.trim(), enabled: true,
      });
      setNcAddLabel(''); setNcAddPhone(''); await loadNc();
      toast.success('Recipient added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setNcBusy(false); }
  };
  const ncToggleRecipient = async (rid, enabled) => {
    setNcBusy(true);
    try {
      await api.put(`/phone-system/client-notifications/recipients/${rid}/enabled`, { enabled });
      await loadNc();
    } catch (e) { toast.error('Save failed'); }
    finally { setNcBusy(false); }
  };
  const ncRemoveRecipient = async (rid) => {
    if (!window.confirm('Remove this recipient?')) return;
    setNcBusy(true);
    try {
      await api.delete(`/phone-system/client-notifications/recipients/${rid}`);
      await loadNc();
      toast.success('Recipient removed');
    } catch (e) { toast.error('Remove failed'); }
    finally { setNcBusy(false); }
  };
  const ncAddEmailRecipient = async () => {
    if (!ncEmailLabel.trim() || !ncEmailAddr.trim()) { toast.error('Label and email address required'); return; }
    setNcBusy(true);
    try {
      await api.post('/phone-system/client-notifications/email-recipients', {
        label: ncEmailLabel.trim(), email: ncEmailAddr.trim(), enabled: true,
      });
      setNcEmailLabel(''); setNcEmailAddr(''); await loadNc();
      toast.success('Email recipient added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setNcBusy(false); }
  };
  const ncToggleEmailRecipient = async (rid, enabled) => {
    setNcBusy(true);
    try {
      await api.put(`/phone-system/client-notifications/email-recipients/${rid}/enabled`, { enabled });
      await loadNc();
    } catch (e) { toast.error('Save failed'); }
    finally { setNcBusy(false); }
  };
  const ncRemoveEmailRecipient = async (rid) => {
    if (!window.confirm('Remove this email recipient?')) return;
    setNcBusy(true);
    try {
      await api.delete(`/phone-system/client-notifications/email-recipients/${rid}`);
      await loadNc();
      toast.success('Email recipient removed');
    } catch (e) { toast.error('Remove failed'); }
    finally { setNcBusy(false); }
  };

  if (loading || !prefs) {
    return <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  const supported = pushSupported();

  return (
    <div className="space-y-5" data-testid="push-prefs-page">
      {/* v3.21-1807 · New Client Notifications area.  Houses SMS + Email
          side by side.  This singleton is INDEPENDENT of
          application_notification_settings (v1707) — toggling here does
          NOT affect Application Notifications and vice versa.  Push
          status/control lives in the "This device" card below. */}
      {ncSettings && !ncSettings.forbidden && (
        <div className="space-y-4" data-testid="new-client-notifications-area">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-4 h-4" /> New Client Notifications
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* ── SMS card ── */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="new-client-sms-alerts-card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-slate-900">New Client SMS</div>
                  <div className="text-xs text-slate-500">
                    One SMS per genuinely new canonical Client.
                  </div>
                </div>
                <Button
                  onClick={ncToggleEnabled}
                  size="sm"
                  disabled={ncBusy}
                  className={ncSettings.enabled ? 'bg-emerald-600 text-white text-xs' : 'bg-slate-200 text-slate-700 text-xs'}
                  data-testid="new-client-sms-enabled-toggle"
                >
                  {ncSettings.enabled ? (<><Bell className="w-3.5 h-3.5 mr-1" /> On</>) : (<><BellOff className="w-3.5 h-3.5 mr-1" /> Off</>)}
                </Button>
              </div>
              {ncSettings.sms_configuration_warning === 'MISSING_PHONE' && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                      data-testid="new-client-sms-missing-phone">
                  New Client SMS is enabled, but no valid notification phone is configured.
                </div>
              )}
              <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                <div className="text-xs font-semibold text-slate-700">Recipients</div>
                {(ncSettings.recipients || []).length === 0 && (
                  <div className="text-xs text-slate-500 italic">No recipients configured.</div>
                )}
                {(ncSettings.recipients || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`new-client-sms-recipient-${r.id}`}>
                    <span className="flex-1">
                      <span className="font-medium">{r.label}</span>
                      <span className="text-slate-500 ml-2 font-mono">{r.phone_masked}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => ncToggleRecipient(r.id, !r.enabled)}
                      className={`h-6 px-2 rounded border ${r.enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'}`}
                      disabled={ncBusy}
                      data-testid={`new-client-sms-recipient-toggle-${r.id}`}
                    >{r.enabled ? 'Enabled' : 'Disabled'}</button>
                    <button
                      type="button"
                      onClick={() => ncRemoveRecipient(r.id)}
                      className="h-6 px-2 rounded border border-rose-200 bg-rose-50 text-rose-700"
                      disabled={ncBusy}
                      data-testid={`new-client-sms-recipient-remove-${r.id}`}
                    ><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="text"
                    placeholder="Label (e.g. Al Davis)"
                    value={ncAddLabel}
                    onChange={(e) => setNcAddLabel(e.target.value)}
                    className="flex-1 h-7 text-xs border rounded px-2"
                    data-testid="new-client-sms-add-label"
                  />
                  <input
                    type="tel"
                    placeholder="+15555550123"
                    value={ncAddPhone}
                    onChange={(e) => setNcAddPhone(e.target.value)}
                    className="flex-1 h-7 text-xs border rounded px-2 font-mono"
                    data-testid="new-client-sms-add-phone"
                  />
                  <Button
                    size="sm"
                    onClick={ncAddRecipient}
                    disabled={ncBusy || !ncAddLabel.trim() || !ncAddPhone.trim()}
                    className="h-7 text-xs bg-slate-900 text-white"
                    data-testid="new-client-sms-add-btn"
                  >Add</Button>
                </div>
              </div>
            </div>

            {/* ── Email card (v1807) ── */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="new-client-email-alerts-card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-slate-900">New Client Email</div>
                  <div className="text-xs text-slate-500">
                    One email per genuinely new canonical Client, via canonical Hub email.
                  </div>
                </div>
                <Button
                  onClick={ncToggleEmailEnabled}
                  size="sm"
                  disabled={ncBusy}
                  className={ncSettings.email_enabled ? 'bg-emerald-600 text-white text-xs' : 'bg-slate-200 text-slate-700 text-xs'}
                  data-testid="new-client-email-enabled-toggle"
                >
                  {ncSettings.email_enabled ? (<><Mail className="w-3.5 h-3.5 mr-1" /> On</>) : (<><BellOff className="w-3.5 h-3.5 mr-1" /> Off</>)}
                </Button>
              </div>
              {ncSettings.email_configuration_warning === 'MISSING_EMAIL' && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                      data-testid="new-client-email-missing-email">
                  New Client Email is enabled, but no valid notification email is configured.
                </div>
              )}
              <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                <div className="text-xs font-semibold text-slate-700">Recipients</div>
                {(ncSettings.email_recipients || []).length === 0 && (
                  <div className="text-xs text-slate-500 italic">No recipients configured.</div>
                )}
                {(ncSettings.email_recipients || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`new-client-email-recipient-${r.id}`}>
                    <span className="flex-1">
                      <span className="font-medium">{r.label}</span>
                      <span className="text-slate-500 ml-2 font-mono">{r.email_masked || '(user-backed)'}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => ncToggleEmailRecipient(r.id, !r.enabled)}
                      className={`h-6 px-2 rounded border ${r.enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'}`}
                      disabled={ncBusy}
                      data-testid={`new-client-email-recipient-toggle-${r.id}`}
                    >{r.enabled ? 'Enabled' : 'Disabled'}</button>
                    <button
                      type="button"
                      onClick={() => ncRemoveEmailRecipient(r.id)}
                      className="h-6 px-2 rounded border border-rose-200 bg-rose-50 text-rose-700"
                      disabled={ncBusy}
                      data-testid={`new-client-email-recipient-remove-${r.id}`}
                    ><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="text"
                    placeholder="Label (e.g. Owner)"
                    value={ncEmailLabel}
                    onChange={(e) => setNcEmailLabel(e.target.value)}
                    className="flex-1 h-7 text-xs border rounded px-2"
                    data-testid="new-client-email-add-label"
                  />
                  <input
                    type="email"
                    placeholder="owner@example.com"
                    value={ncEmailAddr}
                    onChange={(e) => setNcEmailAddr(e.target.value)}
                    className="flex-1 h-7 text-xs border rounded px-2 font-mono"
                    data-testid="new-client-email-add-address"
                  />
                  <Button
                    size="sm"
                    onClick={ncAddEmailRecipient}
                    disabled={ncBusy || !ncEmailLabel.trim() || !ncEmailAddr.trim()}
                    className="h-7 text-xs bg-slate-900 text-white"
                    data-testid="new-client-email-add-btn"
                  >Add</Button>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Bell className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold text-slate-900">Notification Preferences</h1>
          <p className="text-sm text-slate-500">Choose which events push a notification to your subscribed devices. SMTP2GO continues delivering every automated email exactly as before.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-slate-900">This device</div>
            <div className="text-xs text-slate-500" data-testid="push-device-status-text">
              {/* v3.21-1800 · Truthful, permission-aware push status.
                  UI must NEVER report "enabled" merely because
                  Notification.permission === 'granted' — a valid
                  backend registration for the current authenticated
                  user is also required (self-healed silently by
                  <PushAutoSubscribe/> on every authenticated boot). */}
              {!supported && 'Push notifications are not supported in this browser.'}
              {supported && typeof Notification !== 'undefined' && Notification.permission === 'denied' && (
                <span data-testid="push-status-blocked">Push is blocked on this device. Enable notifications in your browser or app settings.</span>
              )}
              {supported && typeof Notification !== 'undefined' && Notification.permission === 'default' && (
                <span data-testid="push-status-permission-required">Push permission required — click Enable on this device.</span>
              )}
              {supported && typeof Notification !== 'undefined' && Notification.permission === 'granted' && !subscribed && (
                <span data-testid="push-status-repair-needed">Push subscription needs repair on this device.</span>
              )}
              {supported && typeof Notification !== 'undefined' && Notification.permission === 'granted' && subscribed && (
                <span data-testid="push-status-enabled">Push enabled on this device — you will receive alerts for the events you enable below.</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={testPush}
              variant="outline"
              size="sm"
              data-testid="push-test-btn"
              disabled={!subscribed}
              className="text-xs"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Send test push
            </Button>
            {(!subscribed || (typeof Notification !== 'undefined' && Notification.permission === 'granted' && !subscribed)) ? (
              <Button
                data-testid={typeof Notification !== 'undefined' && Notification.permission === 'granted' && !subscribed ? 'push-repair-btn' : 'push-enable-btn'}
                size="sm"
                disabled={!supported || (typeof Notification !== 'undefined' && Notification.permission === 'denied')}
                onClick={enablePush}
                className="bg-slate-900 text-white text-xs"
              >
                <Bell className="w-3.5 h-3.5 mr-1" />
                {typeof Notification !== 'undefined' && Notification.permission === 'granted' && !subscribed
                  ? 'Repair Push'
                  : 'Enable on this device'}
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="w-3.5 h-3.5" /> Subscribed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* v3.21-1614 · Android Push connection diagnostic card */}
      {diagnostics && (
        <div
          className="rounded-2xl border border-slate-200 bg-white p-4"
          data-testid="push-diagnostics-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Smartphone className="w-4 h-4" /> Android Push Diagnostics
            </div>
            <span
              className={
                'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ' +
                (diagnostics.is_connected
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                  : 'bg-rose-100 text-rose-800 border border-rose-300')
              }
              data-testid={
                diagnostics.is_connected
                  ? 'push-status-connected'
                  : 'push-status-not-connected'
              }
            >
              {diagnostics.is_connected ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div className="flex justify-between border-b border-slate-100 py-1">
              <dt className="text-slate-500">VAPID public key</dt>
              <dd className={diagnostics.vapid_public_key_configured ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>
                {diagnostics.vapid_public_key_configured ? 'Configured' : 'Missing'}
              </dd>
            </div>
            <div className="flex justify-between border-b border-slate-100 py-1">
              <dt className="text-slate-500">VAPID private key</dt>
              <dd className={diagnostics.vapid_private_key_configured ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>
                {diagnostics.vapid_private_key_configured ? 'Configured' : 'Missing'}
              </dd>
            </div>
            <div className="flex justify-between border-b border-slate-100 py-1">
              <dt className="text-slate-500">Subscriptions on file</dt>
              <dd className="text-slate-900 font-semibold">{diagnostics.subscription_count}</dd>
            </div>
            <div className="flex justify-between border-b border-slate-100 py-1">
              <dt className="text-slate-500">Notification permission</dt>
              <dd className="text-slate-900 font-semibold">
                {typeof Notification !== 'undefined' ? Notification.permission : 'unavailable'}
              </dd>
            </div>
          </dl>
          {diagnostics.subscriptions?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                Devices
              </div>
              <ul className="text-xs space-y-1">
                {diagnostics.subscriptions.map((s) => (
                  <li key={s.id} className="flex items-center justify-between border-b border-slate-100 py-1" data-testid={`diag-sub-${s.id}`}>
                    <span className="text-slate-700">
                      <span className="font-semibold">{s.device_label || '(unlabelled)'}</span>
                      {' · '}
                      <span className="text-slate-500">{s.endpoint_host || 'unknown host'}</span>
                    </span>
                    <span className="text-slate-500">
                      last seen {(s.last_seen || s.created_at || '').slice(0, 19).replace('T', ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {diagnostics.recent_events?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                Last 5 delivery attempts
              </div>
              <ul className="text-xs space-y-1">
                {diagnostics.recent_events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between border-b border-slate-100 py-1">
                    <span className="text-slate-700">
                      <span className="font-semibold">{e.event_type}</span> · {e.status}
                      {e.status_code ? ` (${e.status_code})` : ''}
                    </span>
                    <span className="text-slate-500">{(e.at || '').slice(0, 19).replace('T', ' ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Event triggers</div>
        {EVENT_META.map((e) => {
          const on = !!prefs.events[e.key];
          return (
            <div key={e.key}
                 data-testid={`push-event-${e.key}`}
                 className="flex items-start justify-between gap-4 px-4 py-3 border-t border-slate-100 first:border-t-0">
              <div>
                <div className="text-sm font-semibold text-slate-900">{e.label}</div>
                <div className="text-xs text-slate-500 max-w-xl">{e.desc}</div>
              </div>
              <button
                data-testid={`push-toggle-${e.key}`}
                onClick={() => toggle(e.key)}
                disabled={saving}
                className={
                  'relative w-11 h-6 rounded-full transition ' +
                  (on ? 'bg-emerald-500' : 'bg-slate-300')
                }
              >
                <span className={
                  'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition ' +
                  (on ? 'translate-x-5' : '')
                }/>
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Smartphone className="w-4 h-4" /> Subscribed devices
        </div>
        {devices.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="push-devices-empty">
            No devices subscribed yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Label</th>
                <th className="px-4 py-2 text-left">User-agent</th>
                <th className="px-4 py-2 text-left">Subscribed</th>
                <th className="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id} data-testid={`push-device-${d.id}`}
                    className="border-t border-slate-100">
                  <td className="px-4 py-2 font-semibold text-slate-800">{d.device_label || '(unlabelled)'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-md">{d.user_agent || '—'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{(d.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => removeDevice(d.id)}
                      data-testid={`push-remove-${d.id}`}
                      className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 text-xs font-semibold"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
